import type { OrganizationDetail } from '@/lib/api/apiTypes';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { useOrganization } from '@/features/workspaces/useOrganization';
import { useWorkspaceMode } from '@/features/workspaces/useWorkspaceMode';
import * as React from 'react';

type OrganizationVenue = OrganizationDetail['venues'][number];

export function useBusinessWorkspace(organizationID: string) {
  const workspaceMode = useWorkspaceMode();
  const { currentContext, setBusinessContext } = useAccountContext();
  const organizationQuery = useOrganization(organizationID, workspaceMode.orgAccessEnabled);
  const availableVenues = organizationQuery.data?.venues ?? [];
  const firstVenueID = availableVenues[0]?.venue_id ?? null;
  const defaultVenueID =
    currentContext?.kind === 'business' && currentContext.activeOrgId === organizationID
      ? currentContext.activeVenueId
      : firstVenueID;
  const [activeVenueID, setActiveVenueID] = React.useState<string | null>(defaultVenueID);
  const activeVenue =
    availableVenues.find((venue) => venue.venue_id === activeVenueID) ?? availableVenues[0] ?? null;
  const viewerRole = organizationQuery.data?.viewer_role;

  React.useEffect(() => {
    setActiveVenueID(defaultVenueID);
  }, [defaultVenueID]);

  React.useEffect(() => {
    if (!organizationID || !workspaceMode.orgAccessEnabled || !activeVenue?.venue_id) {
      return;
    }

    void setBusinessContext(organizationID, activeVenue.venue_id);
  }, [activeVenue?.venue_id, organizationID, setBusinessContext, workspaceMode.orgAccessEnabled]);

  const selectVenue = React.useCallback(
    async (venueID: string) => {
      setActiveVenueID(venueID);
      await setBusinessContext(organizationID, venueID);
    },
    [organizationID, setBusinessContext]
  );

  return {
    organizationQuery,
    availableVenues,
    activeVenue,
    activeVenueID,
    viewerRole,
    canManageVenues:
      viewerRole === 'owner' || viewerRole === 'manager',
    canManageEvents:
      viewerRole === 'owner' || viewerRole === 'manager' || viewerRole === 'social',
    canInvite:
      viewerRole === 'owner' || viewerRole === 'manager',
    canViewAnalytics:
      viewerRole === 'owner' || viewerRole === 'manager' || viewerRole === 'social',
    orgAccessEnabled: workspaceMode.orgAccessEnabled,
    selectVenue,
    setActiveVenueID,
  };
}

export type { OrganizationVenue };
